import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import OAuth2Service from '../services/oauth2Service';
import awsConfig from '../aws-config';

// Define the shape of the user object
export interface AuthUser {
  username: string;
  attributes: {
    email: string;
    given_name?: string;
    family_name?: string;
    sub: string;
    [key: string]: any;
  };
  signInUserSession: {
    idToken: {
      jwtToken: string;
      payload: {
        'cognito:groups'?: string[];
        [key: string]: any;
      };
    };
  };
}

// Define the Auth context type
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signInWithFederate: () => void;
  handleOAuth2Callback: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

// Create the context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize OAuth2 service
  const oauth2Service = new OAuth2Service({
    cognitoDomain: awsConfig.cognito.domain || '',
    clientId: awsConfig.cognito.userPoolWebClientId || '',
    redirectUri: awsConfig.cognito.redirectSignIn || '',
    identityProvider: 'FederateIdentityProvider',
    userPoolId: awsConfig.cognito.userPoolId || ''
  });

  // Check auth state on mount and when Auth changes
  useEffect(() => {
    checkUserAuthentication();
  }, []);

  // Function to check if user is authenticated
  const checkUserAuthentication = async () => {
    setIsLoading(true);
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
      setIsAuthenticated(true);
      
      // Check if user is admin
      const groups = currentUser.signInUserSession.idToken.payload['cognito:groups'] || [];
      setIsAdmin(groups.includes('Admins'));
    } catch (error) {
      console.log('User not authenticated', error);
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setError(null); // Reset any previous errors
    } finally {
      setIsLoading(false);
    }
  };

  // Sign in function
  const signIn = async (email: string, password: string): Promise<AuthUser> => {
    try {
      setError(null);
      const user = await Auth.signIn(email, password);
      await checkUserAuthentication();
      return user;
    } catch (error: any) {
      console.error('Error signing in:', error);
      setError(error.message || 'Failed to sign in');
      throw error;
    }
  };

  // Sign out function
  const signOut = async (): Promise<void> => {
    try {
      setError(null);
      await Auth.signOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } catch (error: any) {
      console.error('Error signing out:', error);
      setError(error.message || 'Failed to sign out');
      throw error;
    }
  };

  // Federated sign in function
  const signInWithFederate = (): void => {
    try {
      setError(null);
      oauth2Service.redirectToFederatedLogin();
    } catch (error: any) {
      console.error('Error initiating federated login:', error);
      setError(error.message || 'Failed to initiate federated login');
    }
  };

  // Handle OAuth2 callback - memoized to prevent recreation on every render
  const handleOAuth2Callback = useCallback(async (code: string): Promise<void> => {
    try {
      // Reset auth state at the beginning to prevent inconsistent state
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setError(null);
      setIsLoading(true);

      console.log('Starting OAuth2 callback processing with code length:', code?.length);

      // Complete the OAuth2 flow
      const { user: cognitoUser, session, decodedToken } = await oauth2Service.completeOAuth2Flow(code);

      // Validate the session
      const isValid = await oauth2Service.validateSession(cognitoUser, session);
      if (!isValid) {
        console.error('Session validation failed');
        throw new Error('Session validation failed');
      }

      console.log('Session validation successful');

      // Log full decoded token for analysis
      console.log('Full decoded token for Midway user:', JSON.stringify(decodedToken, null, 2));
      console.log('Available claims for admin determination:', 
        Object.keys(decodedToken).filter(key => 
          typeof key === 'string' && 
          (key.includes('role') || key.includes('group') || key.includes('admin') || key.includes('permission'))
        )
      );

      // Create the AuthUser object compatible with existing code
      const authUser: AuthUser = {
        username: cognitoUser.getUsername(),
        attributes: {
          email: decodedToken.email,
          given_name: decodedToken.given_name,
          family_name: decodedToken.family_name,
          sub: decodedToken.sub
        },
        signInUserSession: {
          idToken: {
            jwtToken: session.getIdToken().getJwtToken(),
            payload: decodedToken
          }
        }
      };

      // Update state
      setUser(authUser);
      setIsAuthenticated(true);
      
      // Check if user is admin
      const groups = decodedToken['cognito:groups'] || [];
      setIsAdmin(groups.includes('Admins'));

      console.log('OAuth2 authentication successful for user:', decodedToken.email);
    } catch (error: any) {
      console.error('Error handling OAuth2 callback:', error);
      
      // IMPORTANT: Reset auth state immediately and thoroughly
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      
      // Clear any stored data on error
      oauth2Service.clearStoredData();
      
      // Set error for display
      setError(error.message || 'Failed to complete authentication');
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [oauth2Service]); // Only depend on oauth2Service

  // Get ID token for API calls
  const getIdToken = async (): Promise<string | null> => {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAdmin,
        isLoading,
        error,
        signIn,
        signInWithFederate,
        handleOAuth2Callback,
        signOut,
        getIdToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
